<script setup lang="ts">
import GoLivePlayer from '@/components/golive/GoLivePlayer.vue'
import {
  addEventsToMentions,
  removeEventsFromMentions,
} from '@/components/mention-helper'
import { app } from '@/composition/app'
import { IComments } from '@/models/Comments'
import { IPosts, PostType } from '@/models/Posts'
import { ReactionType } from '@/models/Reaction'
import { getNumberAbbreviation } from '@/util/NumberUtils'
import { MobileScrollContainer } from '@ghl/clientportal-core'
import {
  UIButton,
  UIPopover,
  UISkeleton,
  UITextLgMedium,
  UITextMdNormal,
  UITextSmMedium,
} from '@gohighlevel/ghl-ui'
import {
  PropType,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

interface IAttachment {
  _id: string
  file: string
  type: string
}

const props = defineProps({
  post: {
    type: Object as PropType<IPosts>,
    required: false,
    default: null,
  },
  sanitizedContent: {
    type: String,
    required: false,
    default: '',
  },
  contentClass: {
    type: String,
    required: false,
  },
  showLikesModal: {
    type: Boolean,
    default: false,
  },
  commentEditorBottom: {
    type: Number,
    required: false,
    default: 0,
  },
  comments: {
    type: Array,
    required: false,
    default: () => [],
  },
  fetchingComments: {
    type: Boolean,
    required: false,
    default: false,
  },
  loadingMoreComments: {
    type: Boolean,
    required: false,
    default: false,
  },
  commentsNextPage: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'likes-clicked'): void
  (e: 'update-preview', index: number): void
  (e: 'toggle-comment-editor'): void
  (e: 'like'): void
  (e: 'load-more-comments'): void
}>()

const { t } = useI18n()
const router = useRouter()
const postContentRef = ref<HTMLElement | null>(null)
const mentionUserTooltip = ref<HTMLElement | null>(null)

// Lazy load components
const PostAttachments = defineAsyncComponent(
  () => import('../PostAttachments.vue')
)
const ReactionsList = defineAsyncComponent(
  () => import('../../reactions/ReactionsList.vue')
)
const ReactionsView = defineAsyncComponent(
  () => import('../../reactions/ReactionsView.vue')
)
const CommentCard = defineAsyncComponent(
  () => import('../../comments/CommentCard.vue')
)
const CommentButton = defineAsyncComponent(
  () => import('../../common/actionButtons/CommentButton.vue')
)
const CommentsDisabled = defineAsyncComponent(
  () => import('../../common/icons/CommentsDisabled.vue')
)
const LikeButton = defineAsyncComponent(
  () => import('../../common/actionButtons/LikeButton.vue')
)

// Setup mention events internally when content or comments finish rendering
function attachMentionHandlers() {
  nextTick(() => {
    mentionUserTooltip.value = window.document.getElementById(
      'user_profile_tooltip'
    )
    const mentions = postContentRef.value?.querySelectorAll(
      'a[data-type="mention"]'
    )
    if (mentions && mentionUserTooltip.value) {
      addEventsToMentions(
        postContentRef.value,
        mentionUserTooltip.value,
        router
      )
    }
  })
}

onMounted(() => {
  attachMentionHandlers()
})

onBeforeUnmount(() => {
  removeEventsFromMentions(postContentRef?.value as HTMLElement)
})

watch(
  () => props.sanitizedContent,
  () => attachMentionHandlers()
)
watch(
  () => props.fetchingComments,
  val => {
    if (!val) attachMentionHandlers()
  }
)
</script>

<template>
  <div class="post-view-body overflow-y-hidden">
    <!-- Loading State -->
    <div v-if="loading || !post" class="space-y-6 px-4 py-4">
      <!-- Post Title -->
      <UISkeleton height="28px" width="66%" />

      <!-- Post Content -->
      <div class="space-y-3">
        <UISkeleton height="20px" width="92%" />
        <UISkeleton height="20px" width="84%" />
        <UISkeleton height="20px" width="76%" />
      </div>

      <!-- Like/Comment Buttons -->
      <div class="flex items-center gap-6 py-2">
        <div class="flex items-center gap-2">
          <UISkeleton height="24px" width="24px" round />
          <UISkeleton height="16px" width="20px" />
        </div>
        <div class="flex items-center gap-2">
          <UISkeleton height="24px" width="24px" round />
          <UISkeleton height="16px" width="20px" />
        </div>
      </div>

      <!-- Comments Skeleton -->
      <div class="space-y-6 pt-2">
        <div v-for="i in 3" :key="i" class="flex items-start gap-3">
          <UISkeleton height="40px" width="40px" circle />
          <div class="flex-1">
            <!-- Name + time row -->
            <div class="flex items-center gap-2">
              <UISkeleton height="16px" width="128px" />
              <UISkeleton height="8px" width="8px" round />
              <UISkeleton height="16px" width="80px" />
            </div>
            <!-- Comment text lines -->
            <div class="mt-3 space-y-2">
              <UISkeleton height="20px" width="83%" />
              <UISkeleton height="20px" width="66%" />
            </div>
            <!-- Actions row -->
            <div class="mt-4 flex items-center gap-6">
              <div class="flex items-center gap-2">
                <UISkeleton height="20px" width="20px" round />
                <UISkeleton height="16px" width="16px" />
              </div>
              <UISkeleton height="16px" width="56px" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actual Content -->
    <MobileScrollContainer v-else>
      <template #scroll-slot>
        <div
          class="post-view-body-container w-full border-0 border-b border-solid border-communities-border"
        >
          <!-- Post Content -->
          <div
            ref="postContentRef"
            :class="app.state.isMobile ? 'px-4' : 'mx-3'"
          >
            <div
              v-if="app.state.isMobile"
              data-scroll-intercept
              class="pointer-events-none h-[1px] w-full"
            />
            <UITextLgMedium
              class="post-title mt-2 pb-2 text-communities-font-primary md:text-2xl"
            >
              {{ post && post.title }}
            </UITextLgMedium>

            <UITextMdNormal
              v-sanitized-html="{
                html: post?.content,
                afterSanity: html => {
                  addEventsToMentions(html, mentionUserTooltip, router)
                },
              }"
              class="content-description text-communities-font-primary"
            />
            <GoLivePlayer
              v-if="
                post?.postType === PostType.GO_LIVE && post?.metadata?.licenseId
              "
              :post="post"
              :custom-class="'mt-2'"
            />
          </div>

          <!-- Attachments -->
          <div
            v-if="post?.attachments && post.attachments.length > 0"
            class="scrollbar-hide flex overflow-x-scroll py-2"
            :class="app.state.isMobile ? 'px-4' : 'mx-3'"
          >
            <div
              v-for="(file, index) in post.attachments"
              :key="(file as IAttachment)._id"
            >
              <PostAttachments
                :file="file"
                :index="index"
                class="mr-4"
                :total-attachments="post?.attachments?.length"
                @update:show="index => emit('update-preview', index)"
              />
            </div>
          </div>

          <!-- Reactions and Comments Count -->
          <div
            id="like_share_buttons"
            class="item-stretch flex gap-4 py-1 text-sm text-communities-font-secondary"
            :class="
              app.state.isMobile &&
              'mt-4 border-0 border-t border-solid border-communities-border !py-2 px-4'
            "
          >
            <div class="flex items-center justify-center">
              <LikeButton
                :content="post"
                :like-action="() => emit('like')"
                class="py-2 font-bold"
                :class="!app.state.isMobile && 'ml-3'"
              />
              <div
                class="-translate-x-3 text-start text-communities-font-secondary hover:rounded-full hover:text-communities-font-primary"
              >
                <UIPopover
                  placement="bottom-start"
                  :show-arrow="false"
                  :show="app.state.isMobile ? false : undefined"
                >
                  <template #trigger>
                    <UITextSmMedium
                      class="cursor-pointer p-2 font-medium"
                      @click.stop="emit('likes-clicked')"
                    >
                      {{ getNumberAbbreviation(post.reactions?.LIKE || 0) }}
                    </UITextSmMedium>
                  </template>
                  <ReactionsList
                    :content-id="post._id || ''"
                    :reaction-type="ReactionType.LIKE"
                    :content-reactions-count="post.reactions?.LIKE || 0"
                  />
                </UIPopover>
              </div>
              <div v-if="showLikesModal">
                <ReactionsView
                  :content-id="post._id || ''"
                  :reaction-type="ReactionType.LIKE"
                  :content-reactions-count="post.reactions?.LIKE || 0"
                  :show-modal="showLikesModal"
                />
              </div>
            </div>
            <div class="flex items-center pr-4">
              <CommentsDisabled
                v-if="post?.isCommentDisabled"
                class="py-2 pr-4 font-bold"
              />
              <template v-else>
                <CommentButton class="py-2 pr-4 font-bold" />
                <UITextSmMedium class="p-1 text-center font-medium">
                  {{ getNumberAbbreviation(post?.commentsCount || 0) }}
                </UITextSmMedium>
              </template>
            </div>
          </div>
        </div>

        <!-- Comments loading shimmer (comments load after post) -->
        <div v-if="fetchingComments" class="mx-3 my-3 space-y-6">
          <div v-for="i in 3" :key="i" class="flex items-start gap-3">
            <UISkeleton height="40px" width="40px" circle />
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <UISkeleton height="16px" width="128px" />
                <UISkeleton height="8px" width="8px" round />
                <UISkeleton height="16px" width="80px" />
              </div>
              <div class="mt-3 space-y-2">
                <UISkeleton height="20px" width="83%" />
                <UISkeleton height="20px" width="66%" />
              </div>
              <div class="mt-4 flex items-center gap-6">
                <div class="flex items-center gap-2">
                  <UISkeleton height="20px" width="20px" round />
                  <UISkeleton height="16px" width="16px" />
                </div>
                <UISkeleton height="16px" width="56px" />
              </div>
            </div>
          </div>
        </div>

        <!-- Comments Section -->
        <div
          v-else
          id="comments_cards"
          class="py-3"
          :class="{
            'scroll-smooth': true,
          }"
          :style="
            app.state.isMobile
              ? `padding-bottom: ${commentEditorBottom + 120}px!important`
              : undefined
          "
        >
          <CommentCard
            v-for="comment in comments as IComments[]"
            :key="comment._id"
            :comment="comment"
            :parent-post-id="post._id || ''"
            :mention-user-tooltip="mentionUserTooltip"
            :expand-replies="false"
            :disable-interaction="false"
            :is-show-comment-action="true"
            :is-trim-content="false"
          />
          <div
            v-if="commentsNextPage"
            class="flex items-center justify-start pl-4 text-communities-font-secondary"
          >
            <UIButton
              id="show-comment_editor__btn"
              :text="true"
              class="font-sans"
              :loading="loadingMoreComments"
              @click="emit('load-more-comments')"
            >
              <div class="p-2 text-left font-sans text-xs">
                {{ t('post.loadMoreComments') }}
              </div>
            </UIButton>
          </div>
        </div>
      </template>
    </MobileScrollContainer>
  </div>
</template>

<style scoped>
.post-view-body {
  /* Prevent horizontal scroll while allowing vertical */
  overflow-x: hidden;
  overflow-y: hidden;
}

.post-view-body-container {
  /* Prevent content from causing horizontal scroll */
  overflow-x: hidden;
  /* But allow vertical scroll within container */
  overflow-y: visible;
}

#comments_cards {
  /* Allow popovers to be visible but prevent horizontal content overflow */
  overflow-x: hidden;
  overflow-y: visible;
  /* Ensure content stays within bounds */
  width: 100%;
  position: relative;
}

.scrollbar-hide {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.scrollbar-hide::-webkit-scrollbar {
  width: 3px;
}

.scrollbar-hide::-webkit-scrollbar-thumb {
  background-color: transparent;
}

.scrollbar-hide::-webkit-scrollbar-track {
  background-color: transparent;
}

/* Ensure mentions show pointer cursor */
:deep(a[data-type='mention']) {
  cursor: pointer;
}
</style>
